import { ILocalizeRequest, LocalizePositionService, MapSettingsService } from '@addins/core/map';
import { Component, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Coordinate } from '@models/coordinate';
import { Guid } from '@models/guid';
import { Actor } from '@models/imported/SagaSchema/Actor';
import { UnitActivity } from '@models/imported/SagaSchema/UnitActivity';
import { Vehicle } from '@models/imported/SagaSchema/Vehicle';
import { Observable, Subject, Subscription } from 'rxjs';
import { filter, tap } from 'rxjs/operators';
import { IChildAwareResource } from '../../schema/interfaces/IChildAwareResource';
import { UnitResourceProviderService } from '../../services/unit-resource-provider/unit-resource-provider.service';

@Component({
  selector: 'app-unit-activity-collapsible-view',
  templateUrl: './unit-activity-collapsible-view.component.html',
  styleUrls: ['./unit-activity-collapsible-view.component.scss']
})
export class UnitActivityCollapsibleViewComponent implements OnInit, OnDestroy {
  @Input()
  unit: UnitActivity;

  @Input()
  opened: Boolean;

  private _open = new Subject<Guid>();

  @Output()
  get $open(): Observable<Guid> {
    return this._open.asObservable();
  }

  resources: IChildAwareResource[];
  crew: number = 0;
  vehicles: number = 0;

  iconName: string = '';

  private localizeUnitEvent: Subject<ILocalizeRequest> = new Subject();

  private subs: Subscription[] = [];

  constructor(
    private resourceService: UnitResourceProviderService,
    private localizePosition: LocalizePositionService,
    private mapSettings: MapSettingsService
  ) {}

  ngOnInit() {
    this.subs.push(this.localizePosition.listenForLocation(this.localizeUnitEvent.asObservable()));
    this.subs.push(
      this.resourceService.$unitChangedEvent.pipe(filter(unitId => unitId === this.unit.ObjGuid)).subscribe(() => this.refreshResources())
    );
    this.refreshResources();
  }

  private refreshResources() {
    this.getResources().subscribe(() => {
      this.setIcon();
      this.crew = this.resources.filter(resource => resource.resource.$t === Actor.$t).length;
      this.vehicles = this.resources.filter(resource => resource.resource.$t === Vehicle.$t).length;
    });
    this.resourceService.getMobilePositions().subscribe();
  }

  private getResources(): Observable<IChildAwareResource[]> {
    return this.resourceService.getResourcesFor(this.unit.ObjGuid).pipe(tap(res => (this.resources = res)));
  }

  ngOnDestroy() {
    while (this.subs.length > 0) {
      this.subs.pop().unsubscribe();
    }
  }

  private setIcon(): void {
    const mainResource = this.resources.find(resource => resource.resource.ObjGuid == this.unit.MainResourceUId);

    if (!mainResource) {
      return;
    }

    this.iconName = UnitResourceProviderService.getResourceIconName(mainResource.resource.$t);
  }

  localize($event): void {
    this.localizeUnitEvent.next({ coordinates: [this.getLocation()], target: null });
    $event.preventDefault();
    $event.stopPropagation();
  }

  private getLocation(): Coordinate {
    const [x, y] = this.resourceService.getPositionFor(this.unit.ObjGuid);

    return {
      x,
      y,
      epsg: this.mapSettings.getSystemProjection()
    };
  }

  mainClick($event): void {
    if (!this.resources) {
      this.refreshResources();
    }
    this._open.next(this.unit.ObjGuid);
  }
}
